import {
  isAuthoritativeChange,
  SYNC_ENTITY_TYPES
} from '@/domain/models/sync/contract';
import { assertLocalDatabase } from '@/domain/ports/localDatabase';

const ACTIVE_OPERATIONS = Object.freeze(['create', 'update']);

const validatePage = (changes) => {
  if (!Array.isArray(changes)) {
    throw new TypeError('Authoritative changes must be an array');
  }

  changes.forEach((change, index) => {
    if (
      !isAuthoritativeChange(change) ||
      !SYNC_ENTITY_TYPES.includes(change.entity_type) ||
      !['create', 'update', 'delete'].includes(change.op)
    ) {
      throw new TypeError(`Invalid authoritative change at index ${index}`);
    }

    if (change.op !== 'delete' && change.payload?.id !== change.entity_id) {
      throw new TypeError(
        `Authoritative payload id does not match ${change.entity_id}`
      );
    }

    if (
      index > 0 &&
      change.server_version < changes[index - 1].server_version
    ) {
      throw new TypeError('Authoritative changes must be ordered by version');
    }
  });
};

const applyChange = (database, change) =>
  database
    .getSyncRecord(change.entity_type, change.entity_id)
    .then((currentRecord) => {
      // Replayed or stale pages must not regress local authoritative state.
      if (currentRecord?.server_version >= change.server_version) {
        return null;
      }

      const entityOperation = ACTIVE_OPERATIONS.includes(change.op)
        ? database.upsertEntity(change.entity_type, change.payload)
        : database.deleteEntity(change.entity_type, change.entity_id);

      return entityOperation
        .then(() =>
          database.upsertSyncRecord({
            entity_type: change.entity_type,
            entity_id: change.entity_id,
            server_version: change.server_version,
            deleted_at: change.op === 'delete' ? new Date().toISOString() : null
          })
        )
        .then(() => ({
          entity_type: change.entity_type,
          entity_id: change.entity_id,
          op: change.op
        }));
    });

/**
 * Applies one validated pull page atomically through the LocalDatabase port.
 *
 * Validation happens before opening the transaction so malformed protocol data
 * cannot partially mutate local state. Replayed versions are ignored while a
 * successful page advances the cursor to its highest received version.
 *
 * @param {Object} options - Apply operation configuration.
 * @param {Object} options.database - LocalDatabase adapter.
 * @param {Object[]} options.changes - Authoritative pull changes.
 * @returns {Promise<Object>} Applied entity metadata grouped by entity type.
 */
export const applyAuthoritativeChanges = ({ database, changes }) => {
  return Promise.resolve().then(() => {
    assertLocalDatabase(database);
    validatePage(changes);

    if (changes.length === 0) {
      return {};
    }

    return database.transaction((transaction) =>
      changes
        .reduce(
          (promise, change) =>
            promise.then((changedEntitiesByType) =>
              applyChange(transaction, change).then((changedEntity) => {
                if (changedEntity) {
                  const entityType = changedEntity.entity_type;
                  if (!changedEntitiesByType[entityType]) {
                    changedEntitiesByType[entityType] = [];
                  }
                  changedEntitiesByType[entityType].push({
                    entity_id: changedEntity.entity_id,
                    op: changedEntity.op
                  });
                }
                return changedEntitiesByType;
              })
            ),
          Promise.resolve({})
        )
        .then((changedEntitiesByType) =>
          transaction
            .getSyncCursor()
            .then((cursor) =>
              transaction
                .setSyncCursor(
                  Math.max(
                    cursor.last_seen_server_version,
                    ...changes.map(({ server_version }) => server_version)
                  )
                )
                .then(() => changedEntitiesByType)
            )
        )
    );
  });
};
